/**
 * Vendor & Purchase Order Service
 * 
 * Provides database-backed vendor management and purchase order operations
 * using Supabase as the backend.
 */

import { supabase } from '@/lib/supabase';
import { getCurrentOrganizationId } from '@/helpers/auth.helpers';
import type { TrackingStatusTag, OrderingMethod, POStatus, POTrackingEntry, POTrackingPurpose } from '@/data/VendorPOContext';

const PO_LINE_ITEM_BULK_CHUNK_SIZE = 100;
const purchaseOrderCreateInFlight = new Map<string, Promise<DbPurchaseOrder>>();

function looksLikeJobNumber(value: string | null | undefined): boolean {
    return typeof value === 'string' && /^JOB-\d+$/i.test(value.trim());
}

function toUuidOrNull(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(trimmed)
        ? trimmed
        : null;
}

function isUuidLike(value: string | null | undefined): boolean {
    return typeof value === 'string'
        && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

const TRACKING_PURPOSES = new Set<POTrackingPurpose>(["inbound", "outbound", "return", "reship", "replacement", "other"]);

const TRACKING_STATUS_TAGS = new Set<TrackingStatusTag>([
  "Pending", "InfoReceived", "InTransit", "OutForDelivery",
  "Delivered", "Exception", "Expired",
]);

function normalizeTrackingEntries(value: unknown): POTrackingEntry[] {
    if (!Array.isArray(value)) return [];
    const entries = value
        .map((entry) => {
            if (!entry || typeof entry !== 'object') return null;
            const row = entry as Record<string, unknown>;
            const trackingNumber = String(row.trackingNumber ?? row.tracking_number ?? '').trim();
            if (!trackingNumber) return null;
            const purpose = String(row.purpose ?? '').trim() as POTrackingPurpose;
            const rawTag = String(row.trackingStatus ?? '').trim() as TrackingStatusTag;
            return {
                id: String(row.id ?? `trk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
                trackingNumber,
                carrier: String(row.carrier ?? '').trim() || undefined,
                purpose: TRACKING_PURPOSES.has(purpose) ? purpose : 'outbound',
                notes: String(row.notes ?? '').trim() || undefined,
                createdAt: String(row.createdAt ?? row.created_at ?? new Date().toISOString()),
                trackingStatus: TRACKING_STATUS_TAGS.has(rawTag) ? rawTag : undefined,
                currentLocation: String(row.currentLocation ?? '').trim() || undefined,
                estimatedDelivery: String(row.estimatedDelivery ?? '').trim() || undefined,
                lastEventAt: String(row.lastEventAt ?? '').trim() || undefined,
                lastEventDescription: String(row.lastEventDescription ?? '').trim() || undefined,
            } satisfies POTrackingEntry;
        })
        .filter(Boolean);
    return entries as POTrackingEntry[];
}

function buildPoItemSignature(items: Array<{
    sku?: string | null;
    product_name?: string | null;
    quantity_ordered?: number | null;
    unit_cost?: number | null;
}>): string {
    return items
        .map((item) => ({
            sku: String(item.sku || '').trim().toUpperCase(),
            product_name: String(item.product_name || '').trim().toLowerCase(),
            quantity_ordered: Math.max(0, Number(item.quantity_ordered ?? 0)),
            unit_cost: Number(item.unit_cost ?? 0),
        }))
        .sort((a, b) =>
            a.sku.localeCompare(b.sku)
            || a.product_name.localeCompare(b.product_name)
            || a.quantity_ordered - b.quantity_ordered
            || a.unit_cost - b.unit_cost,
        )
        .map((item) => `${item.sku}|${item.product_name}|${item.quantity_ordered}|${item.unit_cost}`)
        .join('||');
}

function buildPurchaseOrderCreateFingerprint(input: {
    organizationId: string;
    jobId: string | null;
    vendorId: string | null;
    vendorName: string;
    itemSignature: string;
}): string {
    return [
        input.organizationId,
        input.jobId || '',
        input.vendorId || input.vendorName.trim().toLowerCase(),
        input.itemSignature,
    ].join('::');
}

async function resolvePoLineProductNames<T extends { sku?: string; product_name?: string }>(
    items: T[],
    vendorId?: string | null,
): Promise<Array<T & { product_name: string }>> {
    if (items.length === 0) return [];

    const skuSet = new Set<string>();
    const uuidNameSet = new Set<string>();
    items.forEach((item) => {
        const sku = String(item.sku || '').trim();
        const rawName = String(item.product_name || '').trim();
        if (sku) skuSet.add(sku);
        if (isUuidLike(rawName)) uuidNameSet.add(rawName);
    });

    const normalizedVendorId = toUuidOrNull(vendorId || undefined);
    const vendorSkuLookup = normalizedVendorId && skuSet.size > 0
        ? supabase
            .from('product_vendor_sku_map')
            .select('vendor_sku, internal_sku, variant_key, confidence, match_type')
            .eq('vendor_id', normalizedVendorId)
            .eq('is_active', true)
            .in('vendor_sku', Array.from(skuSet))
        : Promise.resolve({ data: [], error: null } as const);

    const [vendorSkuResult, bySkuResult, byProductIdResult] = await Promise.all([
        vendorSkuLookup,
        skuSet.size > 0
            ? supabase
                .from('product_registry')
                .select('sku, product_name')
                .in('sku', Array.from(skuSet))
            : Promise.resolve({ data: [], error: null } as const),
        uuidNameSet.size > 0
            ? supabase
                .from('product_registry')
                .select('product_id, product_name')
                .in('product_id', Array.from(uuidNameSet))
            : Promise.resolve({ data: [], error: null } as const),
    ]);

    const internalSkuByVendorSku = new Map<string, string>();
    const candidatesByVendorSku = new Map<string, Array<{ internal_sku: string; confidence: number; match_type: string; variant_key: string }>>();
    if (vendorSkuResult.data?.length) {
        const grouped = new Map<string, Array<{ internal_sku: string; confidence: number; match_type: string; variant_key: string }>>();
        vendorSkuResult.data.forEach((row) => {
            const vendorSku = String((row as { vendor_sku?: unknown }).vendor_sku || '').trim();
            const internalSku = String((row as { internal_sku?: unknown }).internal_sku || '').trim();
            if (!vendorSku || !internalSku) return;
            const confidence = Number((row as { confidence?: unknown }).confidence ?? 0) || 0;
            const matchType = String((row as { match_type?: unknown }).match_type || 'exact');
            const variantKey = String((row as { variant_key?: unknown }).variant_key || '').trim();
            const prev = grouped.get(vendorSku) || [];
            prev.push({ internal_sku: internalSku, confidence, match_type: matchType, variant_key: variantKey });
            grouped.set(vendorSku, prev);
        });
        for (const [vendorSku, matches] of grouped.entries()) {
            candidatesByVendorSku.set(vendorSku, matches);
            matches.sort((a, b) => {
                if (b.confidence !== a.confidence) return b.confidence - a.confidence;
                if (a.match_type === b.match_type) return 0;
                if (a.match_type === 'exact') return -1;
                if (b.match_type === 'exact') return 1;
                if (a.match_type === 'manual_override') return -1;
                if (b.match_type === 'manual_override') return 1;
                return 0;
            });
            internalSkuByVendorSku.set(vendorSku, matches[0].internal_sku);
        }
    }

    const nameBySku = new Map<string, string>();
    const nameByProductId = new Map<string, string>();

    (bySkuResult.data ?? []).forEach((row) => {
        const sku = String((row as { sku?: unknown }).sku || '').trim();
        const name = String((row as { product_name?: unknown }).product_name || '').trim();
        if (sku && name) nameBySku.set(sku, name);
    });
    (byProductIdResult.data ?? []).forEach((row) => {
        const id = String((row as { product_id?: unknown }).product_id || '').trim();
        const name = String((row as { product_name?: unknown }).product_name || '').trim();
        if (id && name) nameByProductId.set(id, name);
    });

    return items.map((item) => {
        const inputSku = String(item.sku || '').trim();
        const rawName = String(item.product_name || '').trim();
        const candidates = candidatesByVendorSku.get(inputSku) || [];
        let resolvedSku = internalSkuByVendorSku.get(inputSku) || inputSku;
        if (candidates.length > 1) {
            const nameLower = rawName.toLowerCase();
            const variantMatches = candidates.filter((c) => c.variant_key && nameLower.includes(c.variant_key.toLowerCase()));
            if (variantMatches.length === 1) {
                resolvedSku = variantMatches[0].internal_sku;
            } else {
                throw new Error(
                    `Ambiguous vendor SKU mapping for "${inputSku}". Multiple internal SKUs exist; include variant context or map manually.`,
                );
            }
        }
        const bySku = resolvedSku ? nameBySku.get(resolvedSku) : undefined;
        const byProductId = rawName ? nameByProductId.get(rawName) : undefined;
        const safeName = (bySku || byProductId || (!isUuidLike(rawName) ? rawName : '') || (!isUuidLike(resolvedSku) ? resolvedSku : '') || 'Unmapped Product').trim();
        return { ...item, sku: resolvedSku, product_name: safeName };
    });
}

async function resolveJobIdForPurchaseOrder(jobIdOrNumber: string | null | undefined): Promise<string | null> {
    if (!jobIdOrNumber) return null;
    const raw = jobIdOrNumber.trim();
    if (!raw) return null;
    if (!looksLikeJobNumber(raw)) return raw;

    const { data, error } = await supabase
        .from('jobs')
        .select('id')
        .eq('job_number', raw)
        .maybeSingle();

    if (error) {
        console.warn('[PurchaseOrderService] Failed to resolve job_number to job UUID:', error);
        return raw;
    }

    return data?.id ?? raw;
}

/**
 * Database types for vendor and purchase order tables
 * These match the Supabase schema after migration
 */

export interface DbVendor {
    id: string;
    organization_id: string;
    name: string;
    code: string;
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    address: string | null;
    website: string | null;
    notes: string | null;
    ordering_method: OrderingMethod | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface DbPurchaseOrder {
    id: string;
    organization_id: string;
    po_number: string;
    external_po_number?: string | null;
    job_id: string | null;
    job_label: string | null;
    vendor_id: string | null;
    vendor: string;
    status: POStatus;
    total_cost: number;
    notes: string | null;
    flagged: boolean;
    flag_reason: string | null;
    created_at: string;
    submitted_at: string | null;
    confirmed_at: string | null;
    delivered_at: string | null;
    created_by: string | null;
    updated_at: string;
    eta_confirmed?: boolean | null;
    vendor_ack_at?: string | null;
    estimated_eta?: string | null;
    confirmed_eta?: string | null;
    ack_reference?: string | null;
    tracking_numbers?: unknown | null;
    warehouse_id?: string | null;
    warehouses?: { name: string } | null;
}

export interface DbPOLineItem {
    id: string;
    po_id: string;
    sku: string;
    product_name: string | null;
    quantity_ordered: number;
    quantity_received: number | null;
    unit_cost: number;
    line_total: number | null;
}

/**
 * Transform database vendor to frontend Vendor type
 */
function toVendor(db: DbVendor) {
    return {
        vendorId: db.id,
        vendorName: db.name,
        contactName: db.contact_name || '',
        phone: db.contact_phone || '',
        email: db.contact_email || '',
        orderingMethod: (db.ordering_method as OrderingMethod) || 'Email',
        notes: db.notes || '',
        active: db.is_active,
    };
}

/**
 * Transform database PO to frontend PurchaseOrder type
 */
function toPurchaseOrder(db: DbPurchaseOrder, items: DbPOLineItem[]) {
    const etaConfirmed = db.eta_confirmed ?? (db.status === "Confirmed" || db.status === "Delivered");
    return {
        poId: db.id,
        poNumber: db.po_number,
        externalPoNumber: db.external_po_number || undefined,
        jobId: db.job_id || '',
        jobLabel: db.job_label || '',
        vendorId: db.vendor_id || '',
        vendorName: db.vendor,
        items: items.map(item => ({
            sku: item.sku,
            productName: item.product_name || '',
            quantity: item.quantity_ordered,
            unitCost: item.unit_cost,
            lineTotal: item.line_total || (item.quantity_ordered * item.unit_cost),
        })),
        status: db.status,
        totalCost: db.total_cost,
        createdAt: db.created_at,
        submittedAt: db.submitted_at || undefined,
        confirmedAt: db.confirmed_at || undefined,
        deliveredAt: db.delivered_at || undefined,
        etaConfirmed,
        vendorAckAt: db.vendor_ack_at || undefined,
        estimatedEta: db.estimated_eta || undefined,
        confirmedEta: db.confirmed_eta || undefined,
        ackReference: db.ack_reference || undefined,
        trackingNumbers: normalizeTrackingEntries(db.tracking_numbers),
        notes: db.notes || '',
        flagged: db.flagged,
        flagReason: db.flag_reason || undefined,
        warehouseId: db.warehouse_id || undefined,
        warehouseName: db.warehouses?.name || undefined,
    };
}

/**
 * Vendor Service - CRUD operations for vendors
 */
export const VendorService = {
    /**
     * Get all vendors from the database
     */
    async getVendors(): Promise<DbVendor[]> {
        const organizationId = await getCurrentOrganizationId();
        const { data, error } = await supabase
            .from('catalog_vendors')
            .select('*')
            .eq('organization_id', organizationId)
            .order('name');

        if (error) {
            console.error('Failed to fetch vendors:', error);
            throw error;
        }

        return data || [];
    },

    /**
     * Get vendor by ID
     */
    async getVendorById(id: string): Promise<DbVendor | null> {
        const { data, error } = await supabase
            .from('catalog_vendors')
            .select('*')
            .eq('id', id)
            .single();
        
        if (error) {
            console.error('Failed to fetch vendor:', error);
            return null;
        }
        
        return data;
    },

    /**
     * Get vendor by name
     */
    async getVendorByName(name: string): Promise<DbVendor | null> {
        const { data, error } = await supabase
            .from('catalog_vendors')
            .select('*')
            .ilike('name', name)
            .single();
        
        if (error) {
            return null;
        }
        
        return data;
    },

    /**
     * Create a new vendor
     */
    async createVendor(input: {
        name: string;
        code: string;
        contact_name?: string;
        contact_email?: string;
        contact_phone?: string;
        ordering_method?: OrderingMethod;
        notes?: string;
        is_active?: boolean;
    }): Promise<DbVendor> {
        const organizationId = await getCurrentOrganizationId();
        const { data, error } = await supabase
            .from('catalog_vendors')
            .insert({
                organization_id: organizationId,
                name: input.name,
                code: input.code,
                contact_name: input.contact_name || null,
                contact_email: input.contact_email || null,
                contact_phone: input.contact_phone || null,
                ordering_method: input.ordering_method || 'Email',
                notes: input.notes || null,
                is_active: input.is_active ?? true,
            })
            .select()
            .single();
        
        if (error) {
            console.error('Failed to create vendor:', error);
            throw error;
        }
        
        return data;
    },

    /**
     * Update a vendor
     */
    async updateVendor(id: string, input: {
        name?: string;
        code?: string;
        contact_name?: string;
        contact_email?: string;
        contact_phone?: string;
        ordering_method?: OrderingMethod;
        notes?: string;
        is_active?: boolean;
    }): Promise<DbVendor> {
        const updateData: Record<string, unknown> = {};
        
        if (input.name !== undefined) updateData.name = input.name;
        if (input.code !== undefined) updateData.code = input.code;
        if (input.contact_name !== undefined) updateData.contact_name = input.contact_name;
        if (input.contact_email !== undefined) updateData.contact_email = input.contact_email;
        if (input.contact_phone !== undefined) updateData.contact_phone = input.contact_phone;
        if (input.ordering_method !== undefined) updateData.ordering_method = input.ordering_method;
        if (input.notes !== undefined) updateData.notes = input.notes;
        if (input.is_active !== undefined) updateData.is_active = input.is_active;
        
        const { data, error } = await supabase
            .from('catalog_vendors')
            .update(updateData)
            .eq('id', id)
            .select()
            .single();
        
        if (error) {
            console.error('Failed to update vendor:', error);
            throw error;
        }
        
        return data;
    },

    /**
     * Delete a vendor (soft delete by setting is_active to false)
     */
    async deleteVendor(id: string): Promise<void> {
        const { error } = await supabase
            .from('catalog_vendors')
            .update({ is_active: false })
            .eq('id', id);
        
        if (error) {
            console.error('Failed to delete vendor:', error);
            throw error;
        }
    },
};

/**
 * Purchase Order Service - CRUD operations for purchase orders
 */
export const PurchaseOrderService = {
    /**
     * Get all purchase orders from the database
     */
    async getPurchaseOrders(): Promise<DbPurchaseOrder[]> {
        const organizationId = await getCurrentOrganizationId();
        const { data, error } = await supabase
            .from('purchase_orders')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Failed to fetch purchase orders:', error);
            throw error;
        }

        return data || [];
    },

    /**
     * Get purchase order by ID with line items
     */
    async getPurchaseOrderById(id: string): Promise<{ order: DbPurchaseOrder; items: DbPOLineItem[] } | null> {
        const { data: order, error: orderError } = await supabase
            .from('purchase_orders')
            .select('*')
            .eq('id', id)
            .single();
        
        if (orderError || !order) {
            console.error('Failed to fetch purchase order:', orderError);
            return null;
        }
        
        const { data: items, error: itemsError } = await supabase
            .from('po_line_items')
            .select('*')
            .eq('po_id', id);
        
        if (itemsError) {
            console.error('Failed to fetch line items:', itemsError);
            return null;
        }
        
        return { order, items: items || [] };
    },

    /**
     * Get purchase orders by vendor ID
     */
    async getPurchaseOrdersByVendor(vendorId: string): Promise<DbPurchaseOrder[]> {
        const { data, error } = await supabase
            .from('purchase_orders')
            .select('*')
            .eq('vendor_id', vendorId)
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error('Failed to fetch purchase orders by vendor:', error);
            throw error;
        }
        
        return data || [];
    },

    /**
     * Get purchase orders by job ID
     */
    async getPurchaseOrdersByJob(jobId: string): Promise<DbPurchaseOrder[]> {
        const resolvedJobId = await resolveJobIdForPurchaseOrder(jobId);
        const { data, error } = await supabase
            .from('purchase_orders')
            .select('*, warehouses(name)')
            .eq('job_id', resolvedJobId)
            .order('created_at', { ascending: false });
        
        if (error) {
            console.error('Failed to fetch purchase orders by job:', error);
            throw error;
        }
        
        return data || [];
    },

    /**
     * Create a new purchase order
     */
    async createPurchaseOrder(input: {
        po_number: string;
        external_po_number?: string;
        job_id?: string;
        job_label?: string;
        vendor_id: string;
        vendor: string;
        status?: POStatus;
        total_cost?: number;
        notes?: string;
        flagged?: boolean;
        flag_reason?: string;
        submitted_at?: string;
        eta_confirmed?: boolean;
        vendor_ack_at?: string;
        estimated_eta?: string;
        confirmed_eta?: string;
        ack_reference?: string;
        tracking_numbers?: POTrackingEntry[];
        warehouse_id?: string | null;
        items: Array<{
            sku: string;
            product_name?: string;
            quantity_ordered: number;
            unit_cost: number;
        }>;
    }): Promise<DbPurchaseOrder> {
        const { items: orderItems, ...orderData } = input;
        const normalizedItems = await resolvePoLineProductNames(orderItems, orderData.vendor_id);
        const resolvedJobId = await resolveJobIdForPurchaseOrder(orderData.job_id);
        const resolvedVendorId = toUuidOrNull(orderData.vendor_id);
        const organizationId = await getCurrentOrganizationId();
        const requestedItemSignature = buildPoItemSignature(normalizedItems);
        const createFingerprint = buildPurchaseOrderCreateFingerprint({
            organizationId,
            jobId: resolvedJobId,
            vendorId: resolvedVendorId,
            vendorName: orderData.vendor,
            itemSignature: requestedItemSignature,
        });
        const existingCreate = purchaseOrderCreateInFlight.get(createFingerprint);
        if (existingCreate) {
            return existingCreate;
        }

        const createPromise = (async (): Promise<DbPurchaseOrder> => {
            if (resolvedJobId && requestedItemSignature) {
                let existingOrdersQuery = supabase
                    .from('purchase_orders')
                    .select('*')
                    .eq('organization_id', organizationId)
                    .eq('job_id', resolvedJobId)
                    .order('created_at', { ascending: false });

                existingOrdersQuery = resolvedVendorId
                    ? existingOrdersQuery.eq('vendor_id', resolvedVendorId)
                    : existingOrdersQuery.eq('vendor', orderData.vendor);

                const { data: existingOrders, error: existingOrdersError } = await existingOrdersQuery;
                if (existingOrdersError) {
                    console.warn('Failed to check for existing purchase order duplicates:', existingOrdersError);
                } else if ((existingOrders ?? []).length > 0) {
                    const existingLineItems = await PurchaseOrderService.getLineItemsForPurchaseOrders(
                        (existingOrders ?? []).map((order) => order.id),
                    );
                    const itemsByPoId = new Map<string, DbPOLineItem[]>();
                    for (const item of existingLineItems) {
                        const list = itemsByPoId.get(item.po_id) ?? [];
                        list.push(item);
                        itemsByPoId.set(item.po_id, list);
                    }

                    const duplicateOrder = (existingOrders ?? []).find((order) => {
                        const signature = buildPoItemSignature(itemsByPoId.get(order.id) ?? []);
                        return signature === requestedItemSignature;
                    });

                    if (duplicateOrder) {
                        return duplicateOrder;
                    }
                }
            }

            const insertData: Record<string, unknown> = {
                organization_id: organizationId,
                po_number: orderData.po_number,
                job_id: resolvedJobId || null,
                job_label: orderData.job_label || null,
                vendor_id: resolvedVendorId,
                vendor: orderData.vendor,
                status: orderData.status || 'Draft',
                total_cost: orderData.total_cost || 0,
                notes: orderData.notes || null,
                flagged: orderData.flagged || false,
                flag_reason: orderData.flag_reason || null,
                eta_confirmed: orderData.eta_confirmed ?? false,
                vendor_ack_at: orderData.vendor_ack_at || null,
                estimated_eta: orderData.estimated_eta || null,
                confirmed_eta: orderData.confirmed_eta || null,
                ack_reference: orderData.ack_reference || null,
            };
            if (orderData.external_po_number) {
                insertData.external_po_number = orderData.external_po_number;
            }
            if (orderData.submitted_at) {
                insertData.submitted_at = orderData.submitted_at;
            }
            if (orderData.tracking_numbers !== undefined) {
                insertData.tracking_numbers = normalizeTrackingEntries(orderData.tracking_numbers);
            }
            if (orderData.warehouse_id) {
                insertData.warehouse_id = orderData.warehouse_id;
            }

            const { data: order, error: orderError } = await supabase
                .from('purchase_orders')
                .insert(insertData)
                .select()
                .single();

            if (orderError) {
                console.error('Failed to create purchase order:', orderError);
                throw orderError;
            }

            if (normalizedItems.length > 0) {
                const lineItems = normalizedItems.map(item => ({
                    po_id: order.id,
                    sku: item.sku,
                    product_name: item.product_name || null,
                    quantity_ordered: item.quantity_ordered,
                    unit_cost: item.unit_cost,
                    line_total: item.quantity_ordered * item.unit_cost,
                }));

                const { error: itemsError } = await supabase
                    .from('po_line_items')
                    .insert(lineItems);

                if (itemsError) {
                    console.error('Failed to create line items:', itemsError);
                    await supabase.from('purchase_orders').delete().eq('id', order.id);
                    throw itemsError;
                }
            }

            return order;
        })();

        purchaseOrderCreateInFlight.set(createFingerprint, createPromise);
        try {
            return await createPromise;
        } finally {
            if (purchaseOrderCreateInFlight.get(createFingerprint) === createPromise) {
                purchaseOrderCreateInFlight.delete(createFingerprint);
            }
        }
    },

    /**
     * Update a purchase order
     */
    async updatePurchaseOrder(id: string, input: {
        status?: POStatus;
        total_cost?: number;
        notes?: string;
        flagged?: boolean;
        flag_reason?: string;
        submitted_at?: string;
        confirmed_at?: string;
        delivered_at?: string;
        eta_confirmed?: boolean;
        vendor_ack_at?: string;
        estimated_eta?: string;
        confirmed_eta?: string;
        ack_reference?: string;
        external_po_number?: string | null;
        tracking_numbers?: POTrackingEntry[];
    }): Promise<DbPurchaseOrder> {
        const updateData: Record<string, unknown> = {};
        
        if (input.status !== undefined) updateData.status = input.status;
        if (input.total_cost !== undefined) updateData.total_cost = input.total_cost;
        if (input.notes !== undefined) updateData.notes = input.notes;
        if (input.flagged !== undefined) updateData.flagged = input.flagged;
        if (input.flag_reason !== undefined) updateData.flag_reason = input.flag_reason;
        if (input.submitted_at !== undefined) updateData.submitted_at = input.submitted_at;
        if (input.confirmed_at !== undefined) updateData.confirmed_at = input.confirmed_at;
        if (input.delivered_at !== undefined) updateData.delivered_at = input.delivered_at;
        if (input.eta_confirmed !== undefined) updateData.eta_confirmed = input.eta_confirmed;
        if (input.vendor_ack_at !== undefined) updateData.vendor_ack_at = input.vendor_ack_at;
        if (input.estimated_eta !== undefined) updateData.estimated_eta = input.estimated_eta;
        if (input.confirmed_eta !== undefined) updateData.confirmed_eta = input.confirmed_eta;
        if (input.ack_reference !== undefined) updateData.ack_reference = input.ack_reference;
        if (input.external_po_number !== undefined) updateData.external_po_number = input.external_po_number || null;
        if (input.tracking_numbers !== undefined) updateData.tracking_numbers = normalizeTrackingEntries(input.tracking_numbers);
        
        const { data, error } = await supabase
            .from('purchase_orders')
            .update(updateData)
            .eq('id', id)
            .select()
            .single();
        
        if (error) {
            console.error('Failed to update purchase order:', error);
            throw error;
        }
        
        return data;
    },

    /**
     * Delete a purchase order (cascades to line items)
     */
    async deletePurchaseOrder(id: string): Promise<void> {
        const { error } = await supabase
            .from('purchase_orders')
            .delete()
            .eq('id', id);
        
        if (error) {
            console.error('Failed to delete purchase order:', error);
            throw error;
        }
    },

    /**
     * Get line items for a purchase order
     */
    async getLineItems(poId: string): Promise<DbPOLineItem[]> {
        const { data, error } = await supabase
            .from('po_line_items')
            .select('*')
            .eq('po_id', poId);
        
        if (error) {
            console.error('Failed to fetch line items:', error);
            throw error;
        }
        
        return data || [];
    },

    /**
     * Get line items for many purchase orders in chunks.
     *
     * The old hydration path queried po_line_items once per purchase order,
     * which creates hundreds of startup requests on project dashboards.
     */
    async getLineItemsForPurchaseOrders(poIds: string[]): Promise<DbPOLineItem[]> {
        const uniqueIds = Array.from(new Set(poIds.filter(Boolean)));
        if (uniqueIds.length === 0) return [];

        const chunks: string[][] = [];
        for (let i = 0; i < uniqueIds.length; i += PO_LINE_ITEM_BULK_CHUNK_SIZE) {
            chunks.push(uniqueIds.slice(i, i + PO_LINE_ITEM_BULK_CHUNK_SIZE));
        }

        const results = await Promise.all(
            chunks.map(async (ids) => {
                const { data, error } = await supabase
                    .from('po_line_items')
                    .select('*')
                    .in('po_id', ids);

                if (error) {
                    console.error('Failed to fetch bulk line items:', error);
                    throw error;
                }

                return data || [];
            }),
        );

        return results.flat();
    },

    /**
     * Create a purchase order line item
     */
    async createLineItem(input: {
        po_id: string;
        sku: string;
        product_name?: string;
        quantity_ordered: number;
        quantity_received?: number;
        unit_cost: number;
    }): Promise<DbPOLineItem> {
        const { data: poForVendor } = await supabase
            .from('purchase_orders')
            .select('vendor_id')
            .eq('id', input.po_id)
            .maybeSingle();
        const [normalized] = await resolvePoLineProductNames([input], poForVendor?.vendor_id || null);
        const { data, error } = await supabase
            .from('po_line_items')
            .insert({
                po_id: input.po_id,
                sku: normalized?.sku || input.sku,
                product_name: normalized?.product_name || null,
                quantity_ordered: input.quantity_ordered,
                quantity_received: input.quantity_received ?? 0,
                unit_cost: input.unit_cost,
                line_total: input.quantity_ordered * input.unit_cost,
            })
            .select()
            .single();

        if (error) {
            console.error('Failed to create line item:', error);
            throw error;
        }

        return data;
    },

    /**
     * Update a purchase order line item
     */
    async updateLineItem(id: string, input: {
        product_name?: string;
        quantity_ordered?: number;
        quantity_received?: number;
        unit_cost?: number;
    }): Promise<DbPOLineItem> {
        const current = await supabase
            .from('po_line_items')
            .select('*')
            .eq('id', id)
            .single();

        if (current.error || !current.data) {
            console.error('Failed to fetch line item for update:', current.error);
            throw current.error;
        }

        const quantityOrdered = input.quantity_ordered ?? current.data.quantity_ordered;
        const unitCost = input.unit_cost ?? current.data.unit_cost;

        const { data: poForVendor } = await supabase
            .from('purchase_orders')
            .select('vendor_id')
            .eq('id', current.data.po_id)
            .maybeSingle();
        const [normalized] = await resolvePoLineProductNames([{
            sku: current.data.sku,
            product_name: input.product_name ?? current.data.product_name ?? undefined,
        }], poForVendor?.vendor_id || null);

        const { data, error } = await supabase
            .from('po_line_items')
            .update({
                product_name: normalized?.product_name ?? current.data.product_name,
                quantity_ordered: quantityOrdered,
                quantity_received: input.quantity_received ?? current.data.quantity_received,
                unit_cost: unitCost,
                line_total: quantityOrdered * unitCost,
            })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            console.error('Failed to update line item:', error);
            throw error;
        }

        return data;
    },

    /**
     * Delete a purchase order line item
     */
    async deleteLineItem(id: string): Promise<void> {
        const { error } = await supabase
            .from('po_line_items')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Failed to delete line item:', error);
            throw error;
        }
    },
};

/**
 * Combined service that provides the interface expected by VendorPOContext
 */
export const VendorPOService = {
    /**
     * Fetch all vendors transformed to the frontend format
     */
    async fetchVendors() {
        const dbVendors = await VendorService.getVendors();
        return dbVendors.map(toVendor);
    },

    /**
     * Fetch all purchase orders transformed to the frontend format
     */
    async fetchPurchaseOrders() {
        const dbOrders = await PurchaseOrderService.getPurchaseOrders();

        const lineItems = await PurchaseOrderService.getLineItemsForPurchaseOrders(
            dbOrders.map((order) => order.id),
        );
        const itemsByPoId = new Map<string, DbPOLineItem[]>();
        for (const item of lineItems) {
            const list = itemsByPoId.get(item.po_id) ?? [];
            list.push(item);
            itemsByPoId.set(item.po_id, list);
        }

        const orders = dbOrders.map((dbOrder) =>
            toPurchaseOrder(dbOrder, itemsByPoId.get(dbOrder.id) ?? []),
        );
        
        return orders;
    },

    /**
     * Fetch purchase orders for a single job. Used by server-scoped routes
     * that defer the global VendorPO provider but still need canonical PO
     * overlays for material alignment.
     */
    async fetchPurchaseOrdersForJob(jobId: string) {
        const dbOrders = await PurchaseOrderService.getPurchaseOrdersByJob(jobId);

        const lineItems = await PurchaseOrderService.getLineItemsForPurchaseOrders(
            dbOrders.map((order) => order.id),
        );
        const itemsByPoId = new Map<string, DbPOLineItem[]>();
        for (const item of lineItems) {
            const list = itemsByPoId.get(item.po_id) ?? [];
            list.push(item);
            itemsByPoId.set(item.po_id, list);
        }

        return dbOrders.map((dbOrder) =>
            toPurchaseOrder(dbOrder, itemsByPoId.get(dbOrder.id) ?? []),
        );
    },

    /**
     * Create a vendor
     */
    async createVendor(input: Parameters<typeof VendorService.createVendor>[0]) {
        const dbVendor = await VendorService.createVendor(input);
        return toVendor(dbVendor);
    },

    /**
     * Update a vendor
     */
    async updateVendor(id: string, input: {
        name?: string;
        code?: string;
        contact_name?: string;
        contact_email?: string;
        contact_phone?: string;
        ordering_method?: OrderingMethod;
        notes?: string;
        is_active?: boolean;
    }) {
        const dbVendor = await VendorService.updateVendor(id, input);
        return toVendor(dbVendor);
    },

    /**
     * Delete a vendor
     */
    async deleteVendor(id: string) {
        await VendorService.deleteVendor(id);
    },

    /**
     * Create a purchase order
     */
    async createPurchaseOrder(input: Parameters<typeof PurchaseOrderService.createPurchaseOrder>[0]) {
        const dbOrder = await PurchaseOrderService.createPurchaseOrder(input);
        const items = await PurchaseOrderService.getLineItems(dbOrder.id);
        return toPurchaseOrder(dbOrder, items);
    },

    /**
     * Update a purchase order
     */
    async updatePurchaseOrder(id: string, input: {
        status?: POStatus;
        total_cost?: number;
        notes?: string;
        flagged?: boolean;
        flag_reason?: string;
        submitted_at?: string;
        confirmed_at?: string;
        delivered_at?: string;
        eta_confirmed?: boolean;
        vendor_ack_at?: string;
        estimated_eta?: string;
        confirmed_eta?: string;
        ack_reference?: string;
        external_po_number?: string | null;
        tracking_numbers?: POTrackingEntry[];
    }) {
        const dbOrder = await PurchaseOrderService.updatePurchaseOrder(id, input);
        const items = await PurchaseOrderService.getLineItems(id);
        return toPurchaseOrder(dbOrder, items);
    },

    /**
     * Load a purchase order and its raw line items
     */
    async getPurchaseOrderWithLineItems(id: string) {
        return PurchaseOrderService.getPurchaseOrderById(id);
    },

    /**
     * Create a purchase order line item
     */
    async createPurchaseOrderLineItem(input: Parameters<typeof PurchaseOrderService.createLineItem>[0]) {
        return PurchaseOrderService.createLineItem(input);
    },

    /**
     * Update a purchase order line item
     */
    async updatePurchaseOrderLineItem(id: string, input: Parameters<typeof PurchaseOrderService.updateLineItem>[1]) {
        return PurchaseOrderService.updateLineItem(id, input);
    },

    /**
     * Delete a purchase order line item
     */
    async deletePurchaseOrderLineItem(id: string) {
        await PurchaseOrderService.deleteLineItem(id);
    },

    /**
     * Delete a purchase order
     */
    async deletePurchaseOrder(id: string) {
        await PurchaseOrderService.deletePurchaseOrder(id);
    },

    /**
     * Assign or clear the destination warehouse on an existing PO
     */
    async updateWarehouseAssignment(poId: string, warehouseId: string | null): Promise<void> {
        const { error } = await supabase
            .from("purchase_orders")
            .update({ warehouse_id: warehouseId ?? null })
            .eq("id", poId);
        if (error) {
            console.error("[VendorPOService] updateWarehouseAssignment failed:", error.message);
            throw error;
        }
    },
};
